import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useAdminSession() {
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    axios.get(`${API}/admin/me`)
      .then(() => setStatus("ok"))
      .catch(() => setStatus("unauthenticated"));
  }, []);

  const logout = useCallback(async () => {
    await axios.post(`${API}/admin/logout`).catch(() => {});
    setStatus("unauthenticated");
  }, []);

  return { status, logout };
}

export function useAdminProducts(enabled) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exchangeRate, setExchangeRate] = useState(17.5);

  const fetchProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/products?include_sold=true`);
      setProducts(res.data);
    } catch {
      toast.error("No se pudieron cargar los productos");
    }
  }, []);

  const fetchExchangeRate = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/exchange-rate`);
      if (res.data?.rate) setExchangeRate(res.data.rate);
    } catch {
      toast.error("No se pudo actualizar la tasa de cambio");
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    Promise.all([fetchProducts(), fetchExchangeRate()]).finally(() => setLoading(false));
  }, [enabled, fetchProducts, fetchExchangeRate]);

  const saveProduct = useCallback(async (payload, existingId) => {
    if (existingId) {
      await axios.put(`${API}/products/${existingId}`, payload);
      toast.success("Producto actualizado");
    } else {
      await axios.post(`${API}/products`, payload);
      toast.success("Producto creado");
    }
    await fetchProducts();
  }, [fetchProducts]);

  const deleteProduct = useCallback(async (product) => {
    if (!window.confirm(`Eliminar "${product.name}"?`)) return;
    try {
      await axios.delete(`${API}/products/${product.id}`);
      toast.success("Eliminado");
      await fetchProducts();
    } catch {
      toast.error("No se pudo eliminar");
    }
  }, [fetchProducts]);

  const markUnsold = useCallback(async (product) => {
    try {
      await axios.put(`${API}/products/${product.id}`, { sold: false });
      toast.success("Regresado a activos");
      await fetchProducts();
    } catch {
      toast.error("No se pudo actualizar");
    }
  }, [fetchProducts]);

  const toUSD = useCallback((mxn) => (exchangeRate > 0 ? (mxn / exchangeRate).toFixed(2) : "---"), [exchangeRate]);

  return { products, loading, exchangeRate, fetchProducts, fetchExchangeRate, saveProduct, deleteProduct, markUnsold, toUSD };
}

export function useAnalytics(active) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/analytics/summary`);
      setData(res.data);
    } catch {
      setData(null);
      toast.error("No se pudieron cargar las estadisticas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (active && !data) refresh();
  }, [active, data, refresh]);

  return { data, loading, refresh };
}
